import { faTags, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { toast } from 'react-toastify';
import RolesContext from '../../contexts/RolesContext';
import { AssetStatus, assetStatusesApi } from '../../utils/api/asset';
import ConfirmationModal from '../Shared/ConfirmationModal/ConfirmationModal';
import AssetStatusModal from './AssetStatusModal';

interface IAssetStatusesAdminProps {
  divisionId: number;
}

const AssetStatusesAdmin: React.FC<IAssetStatusesAdminProps> = ({
  divisionId,
}) => {
  const permCheck = useContext(RolesContext).userHasPermission;
  const canHandleAssets = permCheck('all', 'Asset Types');

  const [statuses, setStatuses] = useState<AssetStatus[]>([]);
  const [openAssetStatusModal, setOpenAssetStatusModal] = useState(false);
  const [editStatus, setEditStatus] = useState<AssetStatus | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AssetStatus | null>(null);

  const loadStatuses = useCallback(() => {
    if (!divisionId) return;
    assetStatusesApi
      .index(divisionId)
      .then(setStatuses)
      .catch(() => toast.error('Failed to load statuses.'));
  }, [divisionId]);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index)
      return;

    const reordered = [...statuses];
    const [moved] = reordered.splice(result.source.index, 1);

    reordered.splice(result.destination.index, 0, moved);

    const withOrder = reordered.map((s, i) => ({ ...s, sort_order: i }));

    setStatuses(withOrder);
    try {
      await assetStatusesApi.reorder(
        withOrder.map((s) => ({ id: s.id, sort_order: s.sort_order })),
      );
      toast.success('Order updated');
    } catch {
      toast.error('Failed to save order.');
      loadStatuses();
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await assetStatusesApi.destroy(deleteTarget.id);
      toast.success('Status deleted');
      setOpenAssetStatusModal(false);
      setEditStatus(null);
      loadStatuses();
    } catch {
      toast.error('Failed to delete status. Please try again.');
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <section className='asset-config'>
      <div className='asset-config__heading'>
        <span className='asset-config__heading-icon'>
          <FontAwesomeIcon icon={faTags} />
        </span>
        <div className='asset-config__heading-text'>
          <h3>
            Asset Statuses
            <span className='asset-config__count'>{statuses.length}</span>
          </h3>
        </div>
        {canHandleAssets && (
          <button
            className='asset-config__add'
            onClick={() => {
              setEditStatus(null);
              setOpenAssetStatusModal(true);
            }}
          >
            + New Status
          </button>
        )}
      </div>

      <div className='asset-config__table-wrap'>
        <table className='asset-config__table asset-config__table--statuses'>
          <thead>
            <tr>
              {canHandleAssets && <th aria-label='drag' />}
              <th>Name</th>
              <th>Color</th>
              <th>Default</th>
              <th aria-label='actions' />
            </tr>
          </thead>
          <tbody>
            {statuses
              .filter((status) => !status.division_id)
              .map((status: AssetStatus) => (
                <tr key={status.id}>
                  {canHandleAssets && (
                    <td className='asset-config__drag-handle'></td>
                  )}
                  <td>
                    <span
                      className='asset-config__pill'
                      style={{
                        backgroundColor: `${status.color || '#9CA3AF'}22`,
                        color: status.color,
                      }}
                    >
                      {status.name}
                    </span>
                  </td>
                  <td>
                    <span className='asset-config__color-cell'>
                      <span
                        className='asset-config__color-cell__dot'
                        style={{
                          backgroundColor: status.color || '#9CA3AF',
                        }}
                      />
                      <span className='asset-config__hex'>
                        {status.color || '—'}
                      </span>
                    </span>
                  </td>
                  <td>
                    {status.is_default ? (
                      <span className='asset-config__badge-soft is-green'>
                        System Default
                      </span>
                    ) : (
                      <span className='asset-config__muted'>—</span>
                    )}
                  </td>
                  <td className='asset-config__row-actions'>
                    <button
                      className='tiny outline'
                      onClick={() => {
                        setEditStatus(status);
                        setOpenAssetStatusModal(true);
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId='asset-statuses'>
              {(provided) => (
                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                  {statuses
                    .filter((status) => status.division_id)
                    .map((status: AssetStatus, index: number) => (
                      <Draggable
                        key={status.id}
                        draggableId={String(status.id)}
                        index={index}
                        isDragDisabled={!canHandleAssets}
                      >
                        {(drag) => (
                          <tr
                            ref={drag.innerRef}
                            {...drag.draggableProps}
                            style={drag.draggableProps.style}
                          >
                            {canHandleAssets && (
                              <td
                                className='asset-config__drag-handle'
                                {...(canHandleAssets
                                  ? drag.dragHandleProps
                                  : {})}
                              >
                                {canHandleAssets && (
                                  <FontAwesomeIcon icon={faGripVertical} />
                                )}
                              </td>
                            )}
                            <td>
                              <span
                                className='asset-config__pill'
                                style={{
                                  backgroundColor: `${status.color || '#9CA3AF'}22`,
                                  color: status.color,
                                }}
                              >
                                {status.name}
                              </span>
                            </td>
                            <td>
                              <span className='asset-config__color-cell'>
                                <span
                                  className='asset-config__color-cell__dot'
                                  style={{
                                    backgroundColor: status.color || '#9CA3AF',
                                  }}
                                />
                                <span className='asset-config__hex'>
                                  {status.color || '—'}
                                </span>
                              </span>
                            </td>
                            <td>
                              {status.is_default ? (
                                <span className='asset-config__badge-soft is-green'>
                                  Default
                                </span>
                              ) : (
                                <span className='asset-config__muted'>—</span>
                              )}
                            </td>
                            <td className='asset-config__row-actions'>
                              <button
                                className='tiny outline'
                                onClick={() => {
                                  setEditStatus(status);
                                  setOpenAssetStatusModal(true);
                                }}
                              >
                                {canHandleAssets ? 'Edit' : 'View'}
                              </button>
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                  {statuses.length === 0 && (
                    <tr>
                      <td colSpan={5}>
                        <div className='asset-config__empty'>
                          <FontAwesomeIcon icon={faTags} />
                          <strong>No statuses yet</strong>
                          <span>
                            Create your first status to start tracking asset
                            lifecycles.
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              )}
            </Droppable>
          </DragDropContext>
        </table>
      </div>

      {openAssetStatusModal && (
        <AssetStatusModal
          divisionId={divisionId}
          status={editStatus}
          length={statuses.length}
          onSaved={() => {
            loadStatuses();
            setOpenAssetStatusModal(false);
          }}
          onClose={() => {
            setEditStatus(null);
            setOpenAssetStatusModal(false);
          }}
          onDelete={() => {
            setDeleteTarget(editStatus);
          }}
        />
      )}

      <ConfirmationModal
        show={!!deleteTarget}
        title={`Delete "${deleteTarget?.name}"?`}
        subTitle='Assets currently using this status will need a new one.'
        buttonText='Delete Status'
        handleConfirm={handleDelete}
        handleClose={() => setDeleteTarget(null)}
      />
    </section>
  );
};

export default AssetStatusesAdmin;
